import { create } from "zustand";
import { persist } from "zustand/middleware";
import { safeLocalStorage } from "../utils/safeStorage";
import { formatPrice, getProductUnitDisplay } from "../utils/mapWooProduct";
import { getDisplayCategory } from "../utils/productDisplay";
import { buildCartKey, resolveCartKey } from "../utils/productColors";

const CART_STORAGE_KEY = "caf-sports-cart";

/** @typedef {{ id: number, cartKey: string, name: string, price: number, formattedPrice?: string, image: string, quantity: number, slug?: string, sku?: string, unit?: string, unitWeight?: string, unitLabel?: string, categoryName?: string, color?: string }} CartItem */

function parsePrice(price) {
  const amount = Number.parseFloat(price);
  return Number.isNaN(amount) ? 0 : amount;
}

export function productToCartItem(product, quantity = 1, options = {}) {
  const category = getDisplayCategory(product.categories);
  const color = options.color || "";
  const cartKey = buildCartKey(product.id, color);

  return {
    id: product.id,
    cartKey,
    name: product.name,
    price: parsePrice(product.price),
    formattedPrice: product.formattedPrice || formatPrice(String(product.price)),
    image: product.image || "",
    quantity: Math.max(1, quantity),
    slug: product.slug,
    sku: product.sku || "",
    unit: product.unit || "",
    unitWeight: product.unitWeight || "",
    unitLabel: product.unitLabel || getProductUnitDisplay(product),
    categoryName: category?.name || "",
    color,
  };
}

function calculateSubtotal(items) {
  return items.reduce((sum, item) => sum + item.price * item.quantity, 0);
}

function findCartItem(items, cartKey) {
  return items.find((item) => resolveCartKey(item) === cartKey);
}

export const useCartStore = create(
  persist(
    (set, get) => ({
      items: [],

      addItem: (product, quantity = 1, options = {}) => {
        const qty = Math.max(1, quantity);
        const color = options.color || "";
        const cartKey = buildCartKey(product.id, color);

        set((state) => {
          const existing = findCartItem(state.items, cartKey);
          if (existing) {
            return {
              items: state.items.map((item) =>
                resolveCartKey(item) === cartKey
                  ? productToCartItem(product, item.quantity + qty, { color })
                  : item,
              ),
            };
          }
          return {
            items: [...state.items, productToCartItem(product, qty, { color })],
          };
        });
      },

      removeItem: (cartKey) => {
        set((state) => ({
          items: state.items.filter((item) => resolveCartKey(item) !== cartKey),
        }));
      },

      updateQuantity: (cartKey, quantity) => {
        const qty = Math.max(1, quantity);
        set((state) => ({
          items: state.items.map((item) =>
            resolveCartKey(item) === cartKey ? { ...item, quantity: qty } : item,
          ),
        }));
      },

      clearCart: () => set({ items: [] }),

      getItemCount: () =>
        get().items.reduce((sum, item) => sum + item.quantity, 0),

      getSubtotal: () => calculateSubtotal(get().items),

      getTotal: () => calculateSubtotal(get().items),
    }),
    { name: CART_STORAGE_KEY, storage: safeLocalStorage },
  ),
);

export function selectCartCount(state) {
  return state.items.reduce((sum, item) => sum + item.quantity, 0);
}
